import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { useApp } from "@/context/AppStore";
import { Panel, AnimatedNumber } from "@/components/common/Widgets";
import { BoilerTwinDiagram } from "@/components/BoilerTwinDiagram";
import { SankeyFlow } from "@/components/SankeyFlow";
import { fmtNum } from "@/lib/format";

const LOSS_COLORS = { stack: "#E63946", radiation: "#fab20b", blowdown: "#1d6fb8", other: "#8BA1C0" };

export default function BoilerTwin() {
  const { snapshot } = useApp();
  if (!snapshot) return <div className="p-10 text-ink-soft font-mono">Loading…</div>;

  const twin = { boiler: snapshot.boiler, losses: snapshot.losses, coal: snapshot.coal, steam: snapshot.steam };
  const bd = snapshot.losses.breakdown;
  const pie = [
    { name: "Stack", value: bd.stack, key: "stack" },
    { name: "Blowdown", value: bd.blowdown, key: "blowdown" },
    { name: "Radiation", value: bd.radiation, key: "radiation" },
    { name: "Other", value: bd.other, key: "other" },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-4 animate-fade-up" data-testid="boiler-twin">
      <div>
        <div className="eyebrow">Boiler & Steam Digital Twin</div>
        <h1 className="font-display font-extrabold text-2xl uppercase tracking-tight text-ink">Coal → Combustion → Steam → Losses</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel eyebrow="Live Process Flow" title="Boiler Twin" className="lg:col-span-2" testid="twin-panel">
          <BoilerTwinDiagram data={twin} />
        </Panel>

        <div className="space-y-4">
          <Panel eyebrow="Heat Balance" title="Loss Breakdown" testid="loss-pie">
            <div style={{ height: 210 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                    {pie.map((e) => <Cell key={e.key} fill={LOSS_COLORS[e.key]} stroke="#050914" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0A1224", border: "1px solid rgba(139,161,192,0.3)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}
                    formatter={(v) => `${v}%`} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center text-[0.7rem] font-mono text-ink-soft">
              Total Boiler Loss <span className="text-alert font-bold">{fmtNum(snapshot.losses.total_loss_pct, 1)}%</span>
            </div>
          </Panel>

          <Panel eyebrow="Steam Network" title="Steam Balance" testid="steam-balance">
            <div className="grid grid-cols-2 gap-3">
              <Mini label="Generated" value={snapshot.steam.generated_tpd} unit="MT" color="#33b76d" />
              <Mini label="Consumed" value={snapshot.steam.consumed_tpd} unit="MT" color="#1d6fb8" />
              <Mini label="Steam Loss" value={snapshot.steam.loss_pct} unit="%" decimals={1} color="#E63946" />
              <Mini label="Condensate Rec." value={snapshot.steam.condensate_recovery_pct} unit="%" decimals={1} color="#fab20b" />
              <Mini label="Steam Yield" value={snapshot.steam.steam_yield} unit="t/t" decimals={2} color="#33b76d" />
              <Mini label="Coal SEC" value={snapshot.sec.coal_sec} unit="t/t" decimals={3} color="#8BA1C0" />
            </div>
          </Panel>
        </div>
      </div>

      <Panel eyebrow="Energy Flow" title="Coal → Steam → Product Sankey" testid="sankey-panel">
        <SankeyFlow data={snapshot.sankey} />
        <div className="text-[0.68rem] font-mono text-ink-soft mt-2 text-center">
          Every tonne of energy lost between coal and product is visible above — the economic chain, not the instrumentation chain.
        </div>
      </Panel>
    </div>
  );
}

function Mini({ label, value, unit, color, decimals = 0 }) {
  return (
    <div className="panel-2 px-3 py-2.5">
      <div className="eyebrow truncate">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-base font-bold tnum" style={{ color }}><AnimatedNumber value={value} decimals={decimals} /></span>
        <span className="text-[0.62rem] text-ink-soft font-mono">{unit}</span>
      </div>
    </div>
  );
}
